import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CustomerOrdersAPI } from '../../Services/ECommerceAPI';
import { getShippingAddressLines, getAccountCustomer } from '../../Utils/orderFormat';
import { toast } from 'react-toastify';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [order, setOrder] = useState(location.state?.order ?? null);
    const [loading, setLoading] = useState(!location.state?.order);

    useEffect(() => {
        if (location.state?.order) {
            setOrder(location.state.order);
            setLoading(false);
            return;
        }
        const token = sessionStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }
        loadOrder();
    }, [id, location.state?.order]);

    const loadOrder = async () => {
        try {
            setLoading(true);
            const data = await CustomerOrdersAPI.getOrderById(parseInt(id));
            setOrder(data);
        } catch (error) {
            console.error('Error loading order:', error);
            toast.error('Failed to load order details');
            navigate('/shop');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="order-confirmation-loading">Loading order...</div>;
    }

    if (!order && !loading) {
        return (
            <div className="order-confirmation-container">
                <p>Order details are only available after checkout. <button onClick={() => navigate('/shop')}>Continue shopping</button></p>
            </div>
        );
    }

    if (!order) {
        return null;
    }

    const account = getAccountCustomer(order);
    const shippingLines = getShippingAddressLines(order);

    return (
        <div className="order-confirmation-container">
            <div className="confirmation-header">
                <div className="success-icon">✓</div>
                <h1>Order Confirmed!</h1>
                <p>Thank you for your order. We've received it and will process it shortly.</p>
            </div>

            <div className="order-details-card">
                <h2>Order Details</h2>
                <div className="order-info">
                    <div className="info-row">
                        <span>Order Number:</span>
                        <span className="order-number">{order.orderNumber}</span>
                    </div>
                    <div className="info-row">
                        <span>Order Date:</span>
                        <span>{new Date(order.orderDate).toLocaleString()}</span>
                    </div>
                    <div className="info-row">
                        <span>Status:</span>
                        <span className={`status-badge ${order.orderStatus?.statusName?.toLowerCase()}`}>
                            {order.orderStatus?.statusName}
                        </span>
                    </div>
                    <div className="info-row">
                        <span>Total Amount:</span>
                        <span className="total-amount">${order.totalAmount.toFixed(2)}</span>
                    </div>
                </div>

                <div className="order-items">
                    <h3>Order Items</h3>
                    {order.orderItems?.map(item => (
                        <div key={item.orderItemID} className="order-item">
                            <div className="item-info">
                                <span className="item-name">{item.productName}</span>
                                <span className="item-qty">Qty: {item.quantity}</span>
                            </div>
                            <span className="item-price">${item.totalPrice.toFixed(2)}</span>
                        </div>
                    ))}
                </div>

                <div className="contact-info-block">
                    <h3>Contact</h3>
                    <div className="contact-lines">
                        <div className="contact-line">
                            <span className="contact-label">Name</span>
                            <span>
                                {account
                                    ? `${account.firstName} ${account.lastName}`.trim() || account.email
                                    : [order.shippingFirstName, order.shippingLastName].filter(Boolean).join(' ') || '—'}
                            </span>
                        </div>
                        <div className="contact-line">
                            <span className="contact-label">Email</span>
                            <span>{account?.email || '— (guest checkout)'}</span>
                        </div>
                        <div className="contact-line">
                            <span className="contact-label">Phone</span>
                            <span>{order.shippingPhone || account?.phone || '—'}</span>
                        </div>
                    </div>
                </div>

                <div className="shipping-info">
                    <h3>Shipping address</h3>
                    <div className="shipping-address-block">
                        {shippingLines.map((line) => (
                            <div key={line.label} className="shipping-address-line">
                                <span className="shipping-line-label">{line.label}</span>
                                <span className="shipping-line-value">{line.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="confirmation-actions">
                {sessionStorage.getItem('token') && (
                    <button onClick={() => navigate('/orders')} className="view-orders-btn">
                        View All Orders
                    </button>
                )}
                <button onClick={() => navigate('/shop')} className="continue-shopping-btn">
                    Continue Shopping
                </button>
            </div>
        </div>
    );
};

export default OrderConfirmation;
